import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button } from 'components/ui';
import { BoxIcon, ReceiptIcon, StoreIcon, PlusIcon, CartIcon, AdjustIcon } from 'components/icons';
import { ROUTES } from 'lib/constants';
import { useAuth } from 'features/auth';
import { useVendor } from 'features/vendor';
import { useVendorCatalog } from 'features/catalog/hooks/useVendorCatalog';
import { useVendorOrders } from 'features/orders/hooks/useVendorOrders';
import { NotificationsPanel } from 'features/notifications';

function StatCard({ label, value, icon: Icon, onClick }) {
  return (
    <Card className="cursor-pointer transition hover:border-brand-300" onClick={onClick}>
      <div className="flex items-center gap-4">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </Card>
  );
}

/** Landing page with at-a-glance shop stats and quick actions. */
export function DashboardHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useVendor();
  const { items } = useVendorCatalog();
  const { orders } = useVendorOrders();

  const name = profile?.businessName || user?.fullName || 'there';
  const catalogCount = items.length;

  const stats = [
    { label: 'Products', value: catalogCount, icon: BoxIcon, to: ROUTES.CATALOG },
    { label: 'Recent orders', value: orders.length, icon: ReceiptIcon, to: ROUTES.ORDERS },
    { label: 'Lifetime orders', value: profile?.totalOrders ?? '—', icon: StoreIcon, to: ROUTES.ORDERS },
  ];

  return (
    <div>
      <PageHeader title={`Welcome, ${name}`} description="Here's a quick look at your shop." />

      <NotificationsPanel />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <StatCard
            key={stat.label}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            onClick={() => navigate(stat.to)}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          leftIcon={<PlusIcon className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.CATALOG_ADD)}
        >
          Add product
        </Button>
        <Button
          variant="secondary"
          leftIcon={<CartIcon className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.INSTORE_SALE)}
        >
          Record sale
        </Button>
        <Button
          variant="secondary"
          leftIcon={<AdjustIcon className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.INVENTORY)}
        >
          Stock adjustment
        </Button>
        <Button variant="secondary" onClick={() => navigate(ROUTES.ORDERS)}>
          View orders
        </Button>
      </div>
    </div>
  );
}

export default DashboardHome;
